import ToolResultBadge from "@/components/tools/ToolResultBadge";
import type { ToolResultSection } from "@/components/tools/ToolResultTable";
import type { ToolsContent } from "@/lib/content/tools";
import type { ToolId } from "@/lib/content/tools/config";
import { formatBoolean, getRiskScoreTone } from "@/lib/tools/formatters";
import { cn } from "@/lib/utils";

export function boolValue(
  value: boolean | undefined | null,
  ui: ToolsContent["toolsUi"],
) {
  return formatBoolean(value, ui);
}

export function displayText(value: string, ui: ToolsContent["toolsUi"]) {
  if (!value || value === ui.na) {
    return ui.na;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function riskBadge(score: number) {
  const tone = getRiskScoreTone(score);
  return (
    <ToolResultBadge
      className={cn(
        "font-medium",
        tone === "danger" &&
          "border-transparent bg-[var(--brand-red-contrast)] text-white",
        tone === "warning" &&
          "border-transparent bg-[var(--brand-orange)] text-[var(--risk-yellow-text)]",
        tone === "success" &&
          "border-[color-mix(in_srgb,var(--ok)_22%,transparent)] bg-[color-mix(in_srgb,var(--ok)_12%,white)] text-[#0a6234]",
      )}
    >
      {score}%
    </ToolResultBadge>
  );
}

function resolveRiskLevelKey(
  value: string,
  ui: ToolsContent["toolsUi"],
): "low" | "medium" | "high" | undefined {
  const lower = value.toLowerCase();
  if (lower === "low" || lower === "medium" || lower === "high") {
    return lower;
  }
  const { riskLevels } = ui.toolResults.shared;
  for (const key of ["low", "medium", "high"] as const) {
    if (riskLevels[key].toLowerCase() === lower) {
      return key;
    }
  }
  return undefined;
}

function profanityRiskBadge(level: string | undefined) {
  if (level === "high") {
    return riskBadge(3);
  }
  if (level === "medium") {
    return riskBadge(2);
  }
  if (level === "low") {
    return riskBadge(0);
  }
  return null;
}

export function applyToolResultVisualBadges(
  toolId: ToolId,
  sections: ToolResultSection[],
  ui: ToolsContent["toolsUi"],
): ToolResultSection[] {
  return sections.map((section) => ({
    ...section,
    rows: section.rows.map((row) => {
      if (
        toolId === "profanity-detection" &&
        row.label === ui.toolResults.profanityDetection.labels.riskLevel
      ) {
        const level =
          typeof row.value === "string" && row.value !== ui.na
            ? resolveRiskLevelKey(row.value, ui)
            : undefined;
        return {
          ...row,
          value: profanityRiskBadge(level) ?? displayText(level ?? ui.na, ui),
        };
      }
      if (
        (toolId === "ip-lookup" &&
          row.label === ui.toolResults.ipLookup.labels.riskScore) ||
        (toolId === "proxy-vpn-check" &&
          row.label === ui.proxyVpnCheck.labels.threatScore)
      ) {
        const text = String(row.value);
        const score = Number.parseInt(text.replace("%", ""), 10);
        return {
          ...row,
          value: Number.isFinite(score) ? riskBadge(score) : row.value,
        };
      }
      return row;
    }),
  }));
}
